import type { Suggestion } from "../types";

const MAX_SUGGESTION_CONTENT = 50_000;
const MAX_SUGGESTION_LABEL = 100;

const isValidRawSuggestion = (value: unknown): value is { content: string; label?: string } => {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  if (typeof v.content !== "string" || v.content.trim().length === 0) return false;
  if (v.content.length > MAX_SUGGESTION_CONTENT) {
    console.warn("[suggestionUtils] Suggestion content exceeds max length — skipping");
    return false;
  }
  if (typeof v.label === "string" && v.label.length > MAX_SUGGESTION_LABEL) {
    console.warn("[suggestionUtils] Suggestion label exceeds max length — truncating");
    v.label = v.label.substring(0, MAX_SUGGESTION_LABEL);
  }
  return true;
};

const fallbackSuggestion = (response: string): Suggestion[] => {
  let fallbackText = response;
  try {
    const parsed = JSON.parse(response) as Record<string, unknown>;
    if (parsed && typeof parsed === "object") {
      const candidateFields = ["content", "text", "improved", "result"] as const;
      for (const key of candidateFields) {
        const value = parsed[key];
        if (typeof value === "string" && value.trim()) {
          fallbackText = value;
          break;
        }
      }
    }
  } catch {
    // Keep raw response as fallback.
  }

  return [{ label: "Improved", content: fallbackText }];
};

export const parseSuggestionsFromResponse = (response: string): Suggestion[] => {
  try {
    const parsed = JSON.parse(response) as
      | Record<string, unknown>
      | Array<Record<string, unknown>>;

    let rawSuggestions: unknown[] = [];
    if (Array.isArray(parsed)) {
      rawSuggestions = parsed;
    } else if (Array.isArray(parsed.suggestions)) {
      rawSuggestions = parsed.suggestions;
    } else if (typeof parsed.content === "string") {
      rawSuggestions = [parsed];
    } else {
      const unknownKeys = Object.keys(parsed).filter(
        (k) => !["suggestions", "content", "label"].includes(k)
      );
      console.warn(
        "[suggestionUtils] No known suggestion shape matched. Unrecognised keys:",
        unknownKeys
      );
    }

    const suggestions = rawSuggestions
      .filter(isValidRawSuggestion)
      .map((entry) => ({
        label: typeof entry.label === "string" ? entry.label : "Improved",
        content: entry.content,
      }));

    if (suggestions.length > 0) return suggestions;
  } catch {
    // Continue to fallback parser below.
  }

  return fallbackSuggestion(response);
};
